/*!
 * The kbd-string splitter (`getKeys`) and the form-input filter pattern in `shouldSkipEvent`
 * (including its list of non-text INPUT types) are adapted from hotkeys-js, which this hook
 * previously depended on.
 *
 * MIT License: https://github.com/jaywcjlove/hotkeys-js/blob/master/LICENSE
 * Copyright (c) 2015-present, Kenny Wong
 * Copyright (c) 2011-2013 Thomas Fuchs (https://github.com/madrobby/keymaster)
 * Source: https://github.com/jaywcjlove/hotkeys-js
 */
import {
  isAccelKey,
  preventDefault,
  useEditor,
  useValue
} from "@tldraw/editor";
import { useEffect } from "react";
import { useActions } from "../context/actions.mjs";
import { useCommentingEnabled } from "./useCommentingEnabled.mjs";
import { useReadonly } from "./useReadonly.mjs";
import { useTools } from "./useTools.mjs";
const SKIP_KBDS = [
  // we set these in useNativeClipboardEvents instead
  "copy",
  "cut",
  "paste",
  // There's also an upload asset action, so we don't want to set the kbd twice
  "asset"
];
function useKeyboardShortcuts() {
  const editor = useEditor();
  const isReadonlyMode = useReadonly();
  const actions = useActions();
  const tools = useTools();
  const commentingEnabled = useCommentingEnabled();
  const isFocused = useValue("is focused", () => editor.getInstanceState().isFocused, [editor]);
  useEffect(() => {
    if (!isFocused) return;
    const registry = [];
    const register = (kbd, onKeyDown, onKeyUp) => {
      const parsed = parseKbd(kbd);
      if (parsed.length === 0) return;
      registry.push({ parsed, onKeyDown, onKeyUp });
    };
    for (const action of Object.values(actions)) {
      if (!action.kbd) continue;
      if (isReadonlyMode && !action.readonlyOk) continue;
      if (SKIP_KBDS.includes(action.id)) continue;
      register(getHotkeysStringFromKbd(action.kbd), (event) => {
        if (areShortcutsDisabled(editor) && !action.isRequiredA11yAction) return;
        preventDefault(event);
        action.onSelect("kbd");
      });
    }
    for (const tool of Object.values(tools)) {
      if (!tool.kbd || !tool.readonlyOk && editor.getIsReadonly()) {
        continue;
      }
      if (SKIP_KBDS.includes(tool.id)) continue;
      if (tool.id === "comment" && !commentingEnabled) continue;
      register(getHotkeysStringFromKbd(tool.kbd), (event) => {
        if (areShortcutsDisabled(editor)) return;
        preventDefault(event);
        tool.onSelect("kbd");
      });
    }
    register(
      ",",
      (e) => {
        if (areShortcutsDisabled(editor)) return;
        if (editor.inputs.keys.has("Comma")) return;
        preventDefault(e);
        editor.focus();
        editor.inputs.keys.add("Comma");
        const { x, y, z } = editor.inputs.getCurrentPagePoint();
        const screenpoints = editor.pageToScreen({ x, y });
        const info = {
          type: "pointer",
          name: "pointer_down",
          point: { x: screenpoints.x, y: screenpoints.y, z },
          shiftKey: e.shiftKey,
          altKey: e.altKey,
          ctrlKey: e.metaKey || e.ctrlKey,
          metaKey: e.metaKey,
          accelKey: isAccelKey(e),
          pointerId: 0,
          button: 0,
          isPen: editor.getInstanceState().isPenMode,
          target: "canvas"
        };
        editor.dispatch(info);
      },
      (e) => {
        if (areShortcutsDisabled(editor)) return;
        if (!editor.inputs.keys.has("Comma")) return;
        editor.inputs.keys.delete("Comma");
        const { x, y, z } = editor.inputs.getCurrentPagePoint();
        const screenPoint = editor.pageToScreen({ x, y });
        const info = {
          type: "pointer",
          name: "pointer_up",
          point: { x: screenPoint.x, y: screenPoint.y, z },
          shiftKey: e.shiftKey,
          altKey: e.altKey,
          ctrlKey: e.metaKey || e.ctrlKey,
          metaKey: e.metaKey,
          accelKey: isAccelKey(e),
          pointerId: 0,
          button: 0,
          isPen: editor.getInstanceState().isPenMode,
          target: "canvas"
        };
        editor.dispatch(info);
      }
    );
    const body = editor.getContainerDocument().body;
    const heldKeyRegistrations = /* @__PURE__ */ new Map();
    const handleKeyDown = (e) => {
      if (shouldSkipEvent(e)) return;
      const code = e.code;
      for (const reg of registry) {
        if (!reg.onKeyDown) continue;
        for (const p of reg.parsed) {
          if (matchesEvent(e, p)) {
            if (code) {
              if (e.repeat) {
                const prev = heldKeyRegistrations.get(code);
                if (prev && prev !== reg) return;
              } else {
                heldKeyRegistrations.set(code, reg);
              }
            }
            reg.onKeyDown(e);
            break;
          }
        }
      }
    };
    const handleKeyUp = (e) => {
      if (e.code) heldKeyRegistrations.delete(e.code);
      if (shouldSkipEvent(e)) return;
      for (const reg of registry) {
        if (!reg.onKeyUp) continue;
        for (const p of reg.parsed) {
          if (matchesEvent(e, p)) {
            reg.onKeyUp(e);
            break;
          }
        }
      }
    };
    body.addEventListener("keydown", handleKeyDown);
    body.addEventListener("keyup", handleKeyUp);
    return () => {
      body.removeEventListener("keydown", handleKeyDown);
      body.removeEventListener("keyup", handleKeyUp);
    };
  }, [actions, tools, isReadonlyMode, editor, isFocused, commentingEnabled]);
}
function areShortcutsDisabled(editor) {
  return editor.menus.hasAnyOpenMenus() || editor.getEditingShapeId() !== null || editor.getCrashingError() || !editor.user.getAreKeyboardShortcutsEnabled();
}
const MODIFIER_ALIASES = {
  shift: "shift",
  "\u21E7": "shift",
  alt: "alt",
  option: "alt",
  "\u2325": "alt",
  ctrl: "ctrl",
  control: "ctrl",
  "\u2303": "ctrl",
  cmd: "meta",
  command: "meta",
  meta: "meta",
  "\u2318": "meta"
};
const KEY_ALIASES = {
  "\u232B": "backspace",
  "\u21A9": "enter",
  return: "enter",
  esc: "escape",
  del: "delete",
  ins: "insert",
  left: "arrowleft",
  right: "arrowright",
  up: "arrowup",
  down: "arrowdown",
  space: " "
};
const SHIFT_KEY_TO_BASE = {
  "<": ",",
  ">": ".",
  "?": "/",
  ":": ";",
  '"': "'",
  "{": "[",
  "}": "]",
  "|": "\\",
  _: "-",
  "+": "=",
  "~": "`",
  "!": "1",
  "@": "2",
  "#": "3",
  $: "4",
  "%": "5",
  "^": "6",
  "&": "7",
  "*": "8",
  "(": "9",
  ")": "0"
};
const PHYSICAL_KEY_MAP = {
  KeyA: "a",
  KeyB: "b",
  KeyC: "c",
  KeyD: "d",
  KeyE: "e",
  KeyF: "f",
  KeyG: "g",
  KeyH: "h",
  KeyI: "i",
  KeyJ: "j",
  KeyK: "k",
  KeyL: "l",
  KeyM: "m",
  KeyN: "n",
  KeyO: "o",
  KeyP: "p",
  KeyQ: "q",
  KeyR: "r",
  KeyS: "s",
  KeyT: "t",
  KeyU: "u",
  KeyV: "v",
  KeyW: "w",
  KeyX: "x",
  KeyY: "y",
  KeyZ: "z",
  Digit0: "0",
  Digit1: "1",
  Digit2: "2",
  Digit3: "3",
  Digit4: "4",
  Digit5: "5",
  Digit6: "6",
  Digit7: "7",
  Digit8: "8",
  Digit9: "9",
  Comma: ",",
  Period: ".",
  Slash: "/",
  Semicolon: ";",
  Quote: "'",
  BracketLeft: "[",
  BracketRight: "]",
  Backslash: "\\",
  Minus: "-",
  Equal: "=",
  Backquote: "`"
};
function parseKbd(kbd) {
  const out = [];
  for (const shortcut of getKeys(kbd)) {
    const parsed = parseShortcut(shortcut);
    if (parsed) out.push(parsed);
  }
  return out;
}
function parseShortcut(shortcut) {
  const parts = shortcut.split("+");
  const result = {
    key: "",
    shift: false,
    alt: false,
    ctrl: false,
    meta: false
  };
  let keyPart = "";
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const isLast = i === parts.length - 1;
    if (!isLast) {
      const modAlias = MODIFIER_ALIASES[part.toLowerCase()];
      if (modAlias) result[modAlias] = true;
    } else {
      keyPart = part;
    }
  }
  if (!keyPart) return null;
  let key = keyPart.toLowerCase();
  if (KEY_ALIASES[key]) key = KEY_ALIASES[key];
  result.key = key;
  return result;
}
function getEventKey(e) {
  let key = e.key.toLowerCase();
  if (e.shiftKey && SHIFT_KEY_TO_BASE[key]) {
    key = SHIFT_KEY_TO_BASE[key];
  }
  return key;
}
function matchesEvent(e, parsed) {
  if (e.shiftKey !== parsed.shift) return false;
  if (e.altKey !== parsed.alt) return false;
  if (e.ctrlKey !== parsed.ctrl) return false;
  if (e.metaKey !== parsed.meta) return false;
  if (e.shiftKey) {
    const digitCode = /^Digit([0-9])$/.exec(e.code);
    if (digitCode) return parsed.key === digitCode[1];
  }
  const eventKey = getEventKey(e);
  if (eventKey === parsed.key) return true;
  if (eventKey.length === 1 && /^[\x20-\x7e]$/.test(eventKey)) return false;
  const codeKey = PHYSICAL_KEY_MAP[e.code];
  return codeKey === parsed.key;
}
function shouldSkipEvent(e) {
  if (e.isComposing) return true;
  const target = e.target;
  if (!target) return false;
  if (target.isContentEditable) return true;
  const tagName = target.tagName;
  if (tagName === "SELECT") return true;
  if (tagName === "TEXTAREA") {
    return !target.readOnly;
  }
  if (tagName === "INPUT") {
    const input = target;
    if (["checkbox", "radio", "range", "button", "file", "reset", "submit", "color"].includes(
      input.type
    )) {
      return false;
    }
    return !input.readOnly;
  }
  return false;
}
function getHotkeysStringFromKbd(kbd) {
  return getKeys(kbd).map((kbd2) => {
    let str = "";
    const shift = kbd2.includes("!");
    const alt = kbd2.includes("?");
    const cmd = kbd2.includes("$");
    const k = kbd2.replace(/[!?$]/g, "");
    if (shift && alt && cmd) {
      str = `cmd+shift+alt+${k},ctrl+shift+alt+${k}`;
    } else if (shift && cmd) {
      str = `cmd+shift+${k},ctrl+shift+${k}`;
    } else if (alt && cmd) {
      str = `cmd+alt+${k},ctrl+alt+${k}`;
    } else if (alt && shift) {
      str = `shift+alt+${k}`;
    } else if (shift) {
      str = `shift+${k}`;
    } else if (alt) {
      str = `alt+${k}`;
    } else if (cmd) {
      str = `cmd+${k},ctrl+${k}`;
    } else {
      str = k;
    }
    return str;
  }).join(",");
}
function getKeys(key) {
  if (typeof key !== "string") key = "";
  key = key.replace(/\s/g, "");
  const keys = key.split(",");
  let index = keys.lastIndexOf("");
  for (; index >= 0; ) {
    keys[index - 1] += ",";
    keys.splice(index, 1);
    index = keys.lastIndexOf("");
  }
  return keys;
}
export {
  areShortcutsDisabled,
  getHotkeysStringFromKbd,
  parseKbd,
  useKeyboardShortcuts
};
//# sourceMappingURL=useKeyboardShortcuts.mjs.map
